import { initializeApp, getApps } from 'firebase/app';
import { getAuth, connectAuthEmulator, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, isSupported, Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export let auth = getAuth(app);
export let db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Connect to emulators only when explicitly enabled
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Anonymous auth for attendees — ensures each browser gets a server-validated UID.
// The UID is used as the attendee's identity (`oderId`) in Firestore rules.
let anonAuthReady: Promise<string> | null = null;

export function ensureAnonymousAuth(): Promise<string> {
  if (anonAuthReady) return anonAuthReady;

  anonAuthReady = new Promise<string>((resolve, reject) => {
    // Timeout: reject if auth doesn't resolve within 10 seconds (e.g. iOS background throttling)
    const timeout = setTimeout(() => {
      anonAuthReady = null; // allow retry
      reject(new Error('Anonymous auth timed out'));
    }, 10000);

    // If already signed in (DJ or anonymous), use that UID
    const unsub = onAuthStateChanged(auth, async (user) => {
      unsub();
      clearTimeout(timeout);
      if (user) {
        resolve(user.uid);
      } else {
        try {
          const cred = await signInAnonymously(auth);
          resolve(cred.user.uid);
        } catch (err) {
          anonAuthReady = null; // allow retry
          reject(err);
        }
      }
    });
  });

  return anonAuthReady;
}

// Allow test identity code to invalidate the cached promise so that
// getAttendeeIdAsync() picks up the newly-signed-in test UID.
export function clearAnonAuthCache(): void {
  anonAuthReady = null;
}

export function initTestFirebaseApp(name: string): void {
  const existing = getApps().find(a => a.name === name);
  console.log('[TestIdentity] initTestFirebaseApp:', name, '| existing:', !!existing, '| total apps:', getApps().length);
  const testApp = existing || initializeApp(firebaseConfig, name);

  auth = getAuth(testApp);
  db = getFirestore(testApp);
  console.log('[TestIdentity] Swapped auth/db to app:', testApp.name);

  if (!existing && import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFirestoreEmulator(db, 'localhost', 8080);
    console.log('[TestIdentity] Connected emulators for:', name);
  }
}

// Firebase Cloud Messaging — lazy + async because isSupported() is a Promise
// and getMessaging() throws in unsupported environments (e.g. Firefox private browsing)
let messagingInstance: Messaging | null = null;
export async function getMessagingInstance(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance;
  const supported = await isSupported();
  if (!supported) return null;
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

export default app;
